import React from 'react';
import { Link } from 'react-router-dom';
import { socialLinks, eventInfo, images } from '../data/mock';
import { Instagram, MapPin, Calendar, Clock, Heart, Ticket, Mail, Globe } from 'lucide-react';
import { Button } from './ui/button';

const Footer = () => {
  const openTickets = () => {
    if (eventInfo.ticketUrl) {
      window.open(eventInfo.ticketUrl, '_blank');
    }
  };

  return (
    <footer className="bg-gradient-to-b from-gray-950 to-black py-12 sm:py-16 border-t border-white/10 relative overflow-hidden">
      {/* Decorative elements - hidden on mobile */}
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-pink-500/5 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-orange-500/5 rounded-full blur-3xl hidden sm:block" />

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-12 mb-10 sm:mb-12">
          {/* Logo & Tagline */}
          <div className="text-center md:text-left flex flex-col items-center md:items-start">
            <img 
              src={images.logo} 
              alt="Aloha Tequila & Arts Festival" 
              className="h-20 sm:h-28 object-contain mb-4 hover:scale-105 transition-transform"
            />
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              Honolulu&apos;s premier outdoor festival featuring agave spirits, street food, music, art & flea market.
            </p>
          </div>

          {/* Event Info */}
          <div className="text-center">
            <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400 font-bold text-lg mb-4">Event Details</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-white/80">
                <Calendar className="w-5 h-5 text-pink-400" />
                <span>{eventInfo.date}</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-white/80">
                <Clock className="w-5 h-5 text-orange-400" />
                <span>{eventInfo.time}</span>
              </div>
              <div className="flex items-center justify-center gap-2 text-white/80">
                <MapPin className="w-5 h-5 text-yellow-400" />
                <span>{eventInfo.location}</span>
              </div>
              <p className="text-white/60 text-sm">{eventInfo.address}</p>
              
              {/* Quick Ticket Button */}
              <Button 
                onClick={openTickets}
                className="mt-4 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-full px-6 py-2 transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto"
              >
                <Ticket className="w-4 h-4" />
                Get Tickets
              </Button>
            </div>
          </div>

          {/* Social & Contact */}
          <div className="text-center md:text-right">
            <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400 font-bold text-lg mb-4">Follow the Party!</h4>
            <div className="flex items-center justify-center md:justify-end gap-4 mb-5">
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow @alohatequilafest on Instagram"
                data-testid="footer-instagram-link"
                className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center text-white hover:scale-110 hover:rotate-6 transition-all duration-300 shadow-lg hover:shadow-pink-500/30"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
            <p className="text-white/70 text-sm mb-3">
              Tag us <span className="text-pink-400 font-semibold">@alohatequilafest</span> · <span className="text-pink-400 font-semibold">#AlohaTequilaFest</span>
            </p>

            {/* Contact Info */}
            <div className="space-y-2 text-sm">
              <a
                href={`mailto:${socialLinks.email}`}
                data-testid="footer-email-link"
                className="flex items-center justify-center md:justify-end gap-2 text-white/80 hover:text-pink-400 transition-colors break-all"
              >
                <Mail className="w-4 h-4 flex-shrink-0 text-pink-400" />
                <span>{socialLinks.email}</span>
              </a>
              <a
                href={socialLinks.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-website-link"
                className="flex items-center justify-center md:justify-end gap-2 text-white/80 hover:text-orange-400 transition-colors"
              >
                <Globe className="w-4 h-4 flex-shrink-0 text-orange-400" />
                <span>www.alohatequilafest.com</span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 sm:pt-8 border-t border-white/10 flex flex-col gap-4">
          {/* Business Info Block — Meta Ads compliance */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 text-white/70 text-xs sm:text-sm leading-relaxed text-center md:text-left">
            <p className="text-white font-bold mb-2">Aloha Tequila & Arts Festival</p>
            <p className="mb-1">Presented by <strong className="text-white">Aloha Cancer Project</strong>, a Honolulu-based 501(c)(3) nonprofit organization.</p>
            <p className="mb-1">
              <strong className="text-white">Event venue:</strong> International Market Place · 2330 Kalakaua Ave · Honolulu, HI 96815
            </p>
            <p>
              <strong className="text-white">Contact:</strong>{' '}
              <a href="tel:+18083012134" className="text-pink-400 hover:text-pink-300 underline">808-301-2134</a> · {' '}
              <a href={`mailto:${socialLinks.email}`} className="text-pink-400 hover:text-pink-300 underline break-all">{socialLinks.email}</a>
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-xs sm:text-sm flex items-center gap-2 text-center md:text-left">
              © {new Date().getFullYear()} Aloha Cancer Project. All rights reserved. Made with
              <Heart className="w-4 h-4 text-pink-500 animate-pulse" />
              in Hawaii
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
              <Link to="/faq" className="text-white/50 hover:text-pink-400 transition-colors">FAQs</Link>
              <Link to="/blog" className="text-white/50 hover:text-pink-400 transition-colors" data-testid="footer-blog-link">Blog</Link>
              <Link to="/press" className="text-white/50 hover:text-pink-400 transition-colors" data-testid="footer-press-link">Press</Link>
              <Link to="/privacy" className="text-white/50 hover:text-pink-400 transition-colors">Privacy</Link>
              <Link to="/terms" className="text-white/50 hover:text-pink-400 transition-colors">Terms</Link>
              <Link to="/refund-policy" className="text-white/50 hover:text-pink-400 transition-colors" data-testid="footer-refund-link">Refund Policy</Link>
              <a href="#contact" className="text-white/50 hover:text-pink-400 transition-colors">Contact</a>
            </div>
          </div>
        </div>

        {/* Legal disclaimer — Meta Ads compliance */}
        <p className="text-center text-white/40 text-[11px] sm:text-xs mt-6 sm:mt-8 max-w-3xl mx-auto leading-relaxed">
          <strong className="text-white/70">21+ event.</strong> Valid government-issued ID required at entry. Tickets are non-refundable but transferable to a guest aged 21+. Please drink responsibly. If you or someone you know needs help with alcohol use, call SAMHSA&apos;s National Helpline 24/7 at{' '}
          <a href="tel:1-800-662-4357" className="text-pink-400 hover:text-pink-300 underline">1-800-662-HELP (4357)</a>.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
